import { type FC, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { useSessionStorage } from 'usehooks-ts'

import { Button, Dialog, DialogContent, DialogTitle } from 'base-ui'

import { useOpenableControls } from '../../generic-hooks/useOpenableControls/useOpenableControls'
import { useGlobalConfigContext } from '../../global-config/context/globalConfigContext'
import { StaticImage } from '../../image/StaticImage'
import { urlResolverGetLogin } from '../../url-handling/urlResolver'
import { useExperimentsContext } from '../components/ExperimentsContext'
import {
  EXPERIMENT_TRACK_EVENT_CLICK_ON_CTA,
  EXPERIMENT_TRACK_EVENT_OWN_BRANDS_OVERLAY_CLICK_ON_CLOSE,
  experimentsTrackEvent,
} from '../eventTracking'
import { EXPERIMENT_VARIATION } from '../model/EXPERIMENT_VARIATION'

import heroImage from './images/hero.png'

const REDPOINTS_MODAL_TIMEOUT = 7000
const REDPOINTS_MODAL_ID = 'RedpointsModal'

type RedpointsModalProps = {
  experimentName: string
  experimentVariation: EXPERIMENT_VARIATION | undefined
}
export const RedpointsModal: FC<RedpointsModalProps> = ({
  experimentName,
  experimentVariation,
}) => {
  const globalConfig = useGlobalConfigContext()
  const { addExperimentKeysForActivation } = useExperimentsContext()
  const { handleClose, handleOpen, isOpen } = useOpenableControls(false)
  const [hasSeenRedpointsModal, setHasSeenRedpointsModal] = useSessionStorage(
    REDPOINTS_MODAL_ID,
    false,
    {
      initializeWithValue: false,
    },
  )

  useEffect(() => {
    const handleShowModal = (): void => {
      if (hasSeenRedpointsModal) {
        return
      }
      if (experimentVariation === EXPERIMENT_VARIATION.V1) {
        handleOpen()
      }
    }
    const showModal = setTimeout(() => {
      addExperimentKeysForActivation(experimentName)
      if (experimentVariation === EXPERIMENT_VARIATION.V1) {
        handleShowModal()
      }
    }, REDPOINTS_MODAL_TIMEOUT)

    return (): void => {
      clearTimeout(showModal)
    }
  }, [
    addExperimentKeysForActivation,
    experimentName,
    experimentVariation,
    handleOpen,
    hasSeenRedpointsModal,
  ])

  const onModalClose = (): void => {
    handleClose()
    setHasSeenRedpointsModal(true)
    experimentsTrackEvent(
      globalConfig,
      EXPERIMENT_TRACK_EVENT_OWN_BRANDS_OVERLAY_CLICK_ON_CLOSE,
    )
  }

  /* eslint-disable react/jsx-no-literals -- Since this is only for IT and will be removed soon, we don't need to add translations. */
  return (
    <Dialog
      onClose={onModalClose}
      open={isOpen}
      slotProps={{
        dialog: {
          className:
            'h-auto pb-3 w-10/12 rounded-xl tablet:w-3/12 w-11/12 overflow-hidden',
        },
      }}
    >
      <div className="relative">
        <DialogTitle
          className="absolute right-0 z-10 bg-transparent p-0 tablet:right-3 tablet:top-3"
          onClose={onModalClose}
        />
      </div>
      <div className="bg-light-brand-high">
        <StaticImage
          alt="new-customer-voucher"
          className="m-auto"
          src={heroImage}
        />
      </div>
      <DialogContent className="relative flex flex-col items-center justify-center overflow-hidden bg-light-primary-low p-0 text-center mobile-sm:rounded-xl">
        <div className="my-2 px-10 italic">
          <h2 className="text-2xl font-medium">
            Crea subito un account Redcare e guadagna
          </h2>
          <span className="my-2 text-4xl font-medium text-dark-brand">
            2.000 <br />
            Redpoints <sup>28</sup>
          </span>
        </div>
        <Button<'a'>
          href={urlResolverGetLogin(globalConfig)}
          onClick={() => {
            experimentsTrackEvent(
              globalConfig,
              EXPERIMENT_TRACK_EVENT_CLICK_ON_CTA,
            )
          }}
          slots={{ root: 'a' }}
        >
          <FormattedMessage id="login.createAccount.headline" />
        </Button>
      </DialogContent>
    </Dialog>
  )
  /* eslint-enable react/jsx-no-literals */
}
